import {
  addDays,
  endOfDay,
  isBefore,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Repeat } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { IconButton } from 'shared/components/ui/icon-button';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { RepeatForm } from 'shared/components/ui/repeat-rrule-form';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { DeleteHabitDialog } from 'shared/dialogs/delete-habit';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';
import { getScheduleLabel } from 'shared/utils/get-schedule-label';
import { getSmartDateLabel } from 'shared/utils/get-smart-date-label';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { useData } from '../../use-data';
import { MetaInteraction } from '../meta-interaction/meta-interaction';
import * as Styled from './meta-deadline.style';

const noop = () => {};

export type MetaDeadlineProps = {
  name: string;
  deadline?: Timestamp | null;
  type: TaskType;
  schedules?: HabitSchedule[] | null;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  onChangeDeadline: (date?: Timestamp) => void;
  onChangeSchedule: (schedules: HabitSchedule[] | null) => void;
  onPremium: () => void;
  createHabitAllowed: boolean;
};

export const MetaDeadline: React.FC<MetaDeadlineProps> = ({
  name,
  deadline,
  schedules,
  type,
  onChangeDeadline,
  onChangeSchedule,
  createHabitAllowed,
  onPremium,
  weekStartsOn,
  dateFormat,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const { createHabitTargetAllowed } = useData();
  const [showDeleteHabitDialog, setShowDeleteHabitDialog] = useState(false);

  const shortcuts = useMemo(
    () => ({
      [DatePresets.Today]: getPlatformShortcut(['Shift', 'T']),
      [DatePresets.Tomorrow]: getPlatformShortcut(['Alt', 'Shift', 'T']),
      [DatePresets.NextWeek]: getPlatformShortcut(['Shift', 'W']),
    }),
    [],
  );

  const {
    menuOpen: repeatFormOpen,
    openMenu: openRepeatForm,
    closeMenu: closeRepeatForm,
  } = useTaskDetailSubmenu();

  const activeSchedule = useActiveSchedule(schedules);

  const label = useMemo(() => {
    if (activeSchedule) {
      return getScheduleLabel(activeSchedule, t, true);
    }

    if (!deadline) {
      return;
    }

    return getSmartDateLabel(deadline, today, t, { enableWeekdays: true });
  }, [activeSchedule, deadline, today, t]);

  const shouldReplaceSchedule = useMemo(
    () =>
      activeSchedule?.startDate && isSameDay(activeSchedule?.startDate, today),
    [activeSchedule?.startDate, today],
  );

  const closeMenus = () => {
    closeMenu();
    closeRepeatForm();
  };

  const _onChangeDeadline = (date?: Timestamp) => {
    onChangeDeadline(date);
    closeMenus();
  };

  const _onChangeSchedule = (schedule: HabitSchedule | null) => {
    if (!schedule) {
      onChangeSchedule(null);
      closeMenus();
      return;
    }

    const endedSchedules = schedules?.filter(({ endDate }) => !!endDate) ?? [];
    const updateSchedules = [];
    const activeSchedule = getActiveSchedule(schedules);
    const newSchedule = {
      ...schedule,
      timeslot: activeSchedule?.timeslot ?? null,
    };

    if (!shouldReplaceSchedule) {
      const endDate = endOfDay(today);
      !!activeSchedule &&
        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule?.plannedOccurrences?.filter(
            ({ date }) => isBefore(date, startOfDay(endDate)),
          ),
        });
    }

    if (shouldReplaceSchedule && activeSchedule) {
      newSchedule.entries = activeSchedule.entries ?? [];
      newSchedule.completions = activeSchedule.completions ?? [];
      newSchedule.skips = activeSchedule.skips ?? null;
    }

    updateSchedules.push(newSchedule);

    onChangeSchedule([...endedSchedules, ...updateSchedules]);
    closeMenus();
  };

  const closeDeleteHabitDialog = () => setShowDeleteHabitDialog(false);

  const deleteSchedules = () => {
    onChangeSchedule(null);
    closeMenus();
    closeDeleteHabitDialog();
  };

  const onClear = () => {
    if (schedules?.length) {
      setShowDeleteHabitDialog(true);
      closeMenus();
      return;
    }

    onChangeDeadline();
    closeMenus();
  };

  useKeysDown(
    ['KeyD'],
    (e) => {
      if (!menuOpen && !repeatFormOpen) {
        e.preventDefault();
        e.stopPropagation();
        openMenu();
      }
    },
    { ignoreInputFields: true, capture: true },
  );

  useKeysDown(
    ['KeyT'],
    (e) => {
      if (menuOpen) {
        return;
      }

      e.preventDefault();
      // If Alt is pressed, set tomorrow, otherwise set today
      if (e.altKey) {
        _onChangeDeadline(endOfDay(addDays(today, 1)));
        return;
      }
      _onChangeDeadline(endOfDay(today));
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['KeyW'],
    (e) => {
      if (menuOpen || !e.shiftKey) {
        // Only trigger on Shift+W
        return;
      }

      e.preventDefault();
      _onChangeDeadline(
        endOfDay(
          startOfWeek(addDays(today, 7), {
            weekStartsOn: weekdaysNumberMap[weekStartsOn],
          }),
        ),
      );
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['KeyU'],
    (e) => {
      if (menuOpen || !e.shiftKey) {
        // Only trigger on Shift+U
        return;
      }

      e.preventDefault();
      _onChangeDeadline(undefined);
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  return (
    <>
      <Styled.Container>
        <MetaInteraction
          ref={buttonRef}
          icon={schedules ? Repeat : Calendar}
          placeholder={t('dialogs.task-detail.meta.deadline.placeholder')}
          label={label}
          onClick={schedules ? openRepeatForm : openMenu}
          onClear={label ? onClear : undefined}
          selected={menuOpen || repeatFormOpen}
        />

        {!schedules && (
          <Styled.IconButtonContainer>
            <IconButton icon={Repeat} onClick={openRepeatForm} />
          </Styled.IconButtonContainer>
        )}

        {repeatFormOpen && (
          <PopupMenu
            referenceElement={buttonRef}
            onOutside={closeMenus}
            position="bottom-start"
          >
            <RepeatForm
              initialRrule={activeSchedule?.rrule?.format}
              initialFrequency={activeSchedule?.frequency}
              weekStartsOn={weekStartsOn}
              type={type}
              onChange={_onChangeSchedule}
              onPremium={onPremium}
              isTargetPremium={!createHabitTargetAllowed}
              isCreateHabitPremium={!createHabitAllowed}
            />
          </PopupMenu>
        )}

        {menuOpen && (
          <DatepickerWithOptions
            referenceElement={buttonRef}
            value={deadline ?? undefined}
            onChange={_onChangeDeadline}
            dateFormat={dateFormat}
            weekStartsOn={weekStartsOn}
            icon={IconOptions.Calendar}
            onOutside={closeMenus}
            canRemoveValue
            shortcuts={shortcuts}
            clearShortcut={getPlatformShortcut(['Shift', 'U'])}
          />
        )}
      </Styled.Container>
      {showDeleteHabitDialog && (
        <DeleteHabitDialog
          open={showDeleteHabitDialog}
          name={name}
          onClose={closeDeleteHabitDialog}
          onSubmit={deleteSchedules}
          onRetry={noop}
        />
      )}
    </>
  );
};
